import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import QuoteBuilder from './pages/QuoteBuilder';
import ProposalView from './pages/ProposalView';
import ProposalViewV2 from './pages/ProposalViewV2';
import EditProposal from './pages/EditProposal';
import UserManagement from './pages/UserManagement';
import AIAssistant from './pages/AIAssistant';
import Profile from './pages/Profile';
import MockupDesigner from './pages/MockupDesigner';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import OrderTracking from './pages/OrderTracking';
import ProductionQueues from './pages/ProductionQueues';
import CompletedOrdersReport from './pages/CompletedOrdersReport';
import PublicOrderTracker from './pages/PublicOrderTracker';
import DesignLibrary from './pages/DesignLibrary';
import Products from './pages/Products';
import PriceMatrices from './pages/PriceMatrices';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PublicPayment from './pages/PublicPayment';
import Purchasing from './pages/Purchasing';
import CustomerLogin from './pages/CustomerLogin';
import CustomerPasswordSetup from './pages/CustomerPasswordSetup';
import CustomerNewOrder from './pages/CustomerNewOrder';
import QuoteRequests from './pages/QuoteRequests';
import CustomerPortal from './pages/CustomerPortal';
import CustomerOrderDetail from './pages/CustomerOrderDetail';
import DesignStudio from './pages/DesignStudio';
import MockupDesignerV2 from './pages/MockupDesignerV2';
import DesignApproval from './pages/DesignApproval';
import StorefrontPage from './pages/StorefrontPage';
import Stores from './pages/Stores';
import StoreDashboard from './pages/StoreDashboard';
import StoreShop from './pages/StoreShop';
import WebsitePreview from './pages/WebsitePreview';
import WebsiteEditor from './pages/WebsiteEditor';
const SITE_PAGES = ['about', 'services', 'gallery', 'contact', 'faq'];
import Leads from './pages/Leads';
import PastWork from './pages/PastWork';
import InternalCatalog from './pages/InternalCatalog';
import CommunicationCenter from './pages/CommunicationCenter';
import PublicDesigner from './pages/PublicDesigner';
import PublicCatalog from './pages/PublicCatalog';
import SuperAdmin from './pages/SuperAdmin';
import CompanySettings from './pages/CompanySettings';
import PlatformHome from './pages/PlatformHome';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SiteUnavailable from './pages/SiteUnavailable';
import ImpersonationBanner from './components/ImpersonationBanner';
import { VerifyEmailBanner } from './components/VerifyEmailBanner';
import { SuperAdminGate } from './components/SuperAdminGate';
import SuperAdminSetup from './pages/SuperAdminSetup';
import Signup from './pages/Signup';
import Welcome from './pages/Welcome';
import SubscriptionGate from './pages/SubscriptionGate';
import { ForgotPassword, ResetPassword, VerifyEmail, AcceptInvite } from './pages/AccountRecovery';
import { loadPublicBrand, setBrand } from './lib/brand';
import { apiOrigin, loadSiteContext, siteMode, appUrl, previewHeaders, getSite } from './lib/site';
import '@/App.css';


const API = `${apiOrigin()}/api`;

// Setup axios defaults
axios.defaults.baseURL = API;
Object.assign(axios.defaults.headers.common, previewHeaders());

// Auth context
export const AuthContext = React.createContext(null);

// Public, customer-facing routes that may be served on a white-label storefront host.
const storeShopRoutes = (prefix = '/store/:slug') => (
  <>
    <Route path={prefix} element={<StoreShop />} />
    <Route path={`${prefix === '/' ? '' : prefix}/checkout`} element={<StoreShop view="checkout" />} />
    <Route path={`${prefix === '/' ? '' : prefix}/order/:orderId`} element={<StoreShop view="order" />} />
    <Route path={`${prefix === '/' ? '' : prefix}/eddm`} element={<StoreShop view="eddm" />} />
    <Route path={`${prefix === '/' ? '' : prefix}/eddm/order/:eddmId`} element={<StoreShop view="eddm-order" />} />
  </>
);

const storefrontRoutes = () => (
  <>
    {storeShopRoutes()}
    <Route path="/track/:orderId" element={<PublicOrderTracker />} />
    <Route path="/design" element={<PublicDesigner />} />
    <Route path="/catalog" element={<PublicCatalog />} />
    <Route path="/payment/success" element={<PaymentSuccess />} />
    <Route path="/payment/cancel" element={<PaymentCancel />} />
    <Route path="/pay/:proposalId" element={<PublicPayment />} />
    <Route path="/approve/:proposalId" element={<DesignApproval />} />
    <Route path="/portal/login" element={<CustomerLogin />} />
    <Route path="/portal/setup" element={<CustomerPasswordSetup />} />
    <Route path="/portal" element={<CustomerPortal />} />
    <Route path="/portal/new" element={<CustomerNewOrder />} />
    <Route path="/portal/order/:orderId" element={<CustomerOrderDetail />} />
    <Route path="/portal/proposal/:proposalId" element={<CustomerPortal />} />
  </>
);

// Redirects the browser to the staff application host (used for /login on storefront hosts).
const SuperAdminAlias = () => { const { tab } = useParams(); return <Navigate to={`/superadmin/${tab}`} replace />; };

const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return <div className="loading-screen" data-testid="external-redirect"><div className="spinner"></div><p>Redirecting…</p></div>;
};

// First landing after self-serve signup → onboarding checklist (one-shot flag set by Signup.jsx).
const PostSignupRedirect = () => {
  useEffect(() => { sessionStorage.removeItem('post_signup'); }, []);
  return <Navigate to="/welcome" replace />;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('combined');
  const [gated, setGated] = useState(false);

  useEffect(() => {
    const id = axios.interceptors.response.use((r) => r, (error) => {
      if (error?.response?.status === 402 && error.response.data?.code === 'subscription_required') setGated(true);
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    // Resolve which surface we are on (marketing / app / admin / storefront), then the tenant branding.
    loadSiteContext().then(() => setMode(siteMode())).then(loadPublicBrand).finally(() => {
    const token = localStorage.getItem('token');
    if (token && !['platform', 'storefront', 'unknown'].includes(siteMode())) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get('/auth/me')
        .then(response => {
          setUser(response.data);
          setGated(Boolean(response.data?.company?.subscription?.blocked));
          if (response.data?.company?.branding) setBrand(response.data.company.branding);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    });
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Preview simulation: the marketing surface never serves the staff app, so hop the tab to the simulated app host.
    if (sessionStorage.getItem('site_preview') === 'platform' && getSite().platform?.preview) {
      sessionStorage.setItem('site_preview', 'app');
      const next = sessionStorage.getItem('post_signup') ? '/welcome' : '/';
      sessionStorage.removeItem('post_signup');
      window.location.replace(next);
      return;
    }
    setUser(userData);
    setGated(Boolean(userData?.company?.subscription?.blocked));
    if (userData?.company?.branding) setBrand(userData.company.branding);
    else axios.get('/auth/me').then((r) => { setUser(r.data); setGated(Boolean(r.data?.company?.subscription?.blocked)); if (r.data?.company?.branding) setBrand(r.data.company.branding); }).catch(() => {});
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setGated(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const isSuperAdminHome = user?.role === 'superadmin' && !user?.impersonating;

  if (mode === 'unknown') return <SiteUnavailable />;

  // Preview/dev: the marketing and app surfaces share one host, so auth pages render in place instead of hopping hosts.
  const sameHostApp = (() => { try { return new URL(appUrl()).origin === window.location.origin; } catch (e) { return true; } })();

  if (mode === 'platform' && !(user && sameHostApp)) {
    return (
      <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PlatformHome />} />
          <Route path="/demo" element={<PlatformHome />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/login" element={sameHostApp ? <Login /> : <ExternalRedirect to={`${appUrl()}/login`} />} />
          <Route path="/signup" element={sameHostApp ? <Signup /> : <ExternalRedirect to={`${appUrl()}/signup`} />} />
          <Route path="/forgot-password" element={sameHostApp ? <ForgotPassword /> : <ExternalRedirect to={`${appUrl()}/forgot-password`} />} />
          <Route path="/reset-password" element={sameHostApp ? <ResetPassword /> : <ExternalRedirect to={`${appUrl()}/reset-password${window.location.search}`} />} />
          <Route path="/verify-email" element={sameHostApp ? <VerifyEmail /> : <ExternalRedirect to={`${appUrl()}/verify-email${window.location.search}`} />} />
          <Route path="/accept-invite" element={sameHostApp ? <AcceptInvite /> : <ExternalRedirect to={`${appUrl()}/accept-invite${window.location.search}`} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      </AuthContext.Provider>
    );
  }

  if (mode === 'store') {
    return (
      <AuthContext.Provider value={{ user: null, login, logout }}>
        <BrowserRouter>
          <Routes>
            {storeShopRoutes('/')}
            {storeShopRoutes('/store/:slug')}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </AuthContext.Provider>
    );
  }

  if (mode === 'storefront') {
    return (
      <AuthContext.Provider value={{ user: null, login, logout }}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<StorefrontPage />} />
            {SITE_PAGES.map((pg) => <Route key={pg} path={`/${pg}`} element={<StorefrontPage />} />)}
            <Route path="/login" element={<ExternalRedirect to={`${appUrl()}/login`} />} />
            <Route path="/superadmin" element={<Navigate to="/" />} />
            {storefrontRoutes()}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </AuthContext.Provider>
    );
  }

  if (mode === 'admin' && !user) {
    return (
      <AuthContext.Provider value={{ user, login, logout }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login superAdminOnly />} />
            <Route path="/superadmin/setup" element={<SuperAdminSetup />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </BrowserRouter>
      </AuthContext.Provider>
    );
  }

  const appOnly = mode === 'app' || mode === 'admin';

  if (user && gated && !isSuperAdminHome && !user.impersonating) {
    return <AuthContext.Provider value={{ user, login, logout }}><SubscriptionGate /></AuthContext.Provider>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <BrowserRouter>
        <ImpersonationBanner />
        <VerifyEmailBanner />
        {isSuperAdminHome ? (
          <Routes>
            <Route path="/superadmin/setup" element={<SuperAdminSetup />} />
            <Route path="/superadmin/website-preview" element={<WebsitePreview />} />
            <Route path="/superadmin/website-editor" element={<WebsiteEditor superadmin />} />
            <Route path="/superadmin" element={<SuperAdmin />} />
            <Route path="/superadmin/:tab" element={<SuperAdmin />} />
            <Route path="/super-admin" element={<Navigate to="/superadmin" replace />} />
            <Route path="/super-admin/:tab" element={<SuperAdminAlias />} />
            <Route path="*" element={<Navigate to="/superadmin" />} />
          </Routes>
        ) : (
        <Routes>
          <Route path="/superadmin/setup" element={mode !== 'app' ? <SuperAdminSetup /> : <Navigate to="/" />} />
          <Route path="/superadmin/*" element={user ? <SuperAdminGate /> : <Navigate to="/login" />} />
          <Route path="/super-admin/*" element={user ? <SuperAdminGate /> : <Navigate to="/login" />} />
          <Route path="/settings" element={user ? <CompanySettings /> : <Navigate to="/login" />} />
          <Route path="/settings/website/preview" element={user ? <WebsitePreview /> : <Navigate to="/login" />} />
          <Route path="/settings/website/editor" element={user ? <WebsiteEditor /> : <Navigate to="/login" />} />
          <Route path="/welcome" element={user ? <Welcome /> : <Navigate to="/login" />} />
          <Route path="/signup" element={!user && mode !== 'admin' ? <Signup /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-invite" element={!user ? <AcceptInvite /> : <Navigate to="/" />} />
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          <Route path="/" element={user ? (sessionStorage.getItem('post_signup') ? <PostSignupRedirect /> : <Dashboard />) : (appOnly ? <Navigate to="/login" /> : <StorefrontPage />)} />
          {!user && !appOnly && SITE_PAGES.map((pg) => <Route key={pg} path={`/${pg}`} element={<StorefrontPage />} />)}
          <Route path="/quote-builder" element={user ? <QuoteBuilder /> : <Navigate to="/login" />} />
          <Route path="/proposal/:id" element={user ? <ProposalViewV2 /> : <Navigate to="/login" />} />
          <Route path="/proposal/:id/legacy" element={user ? <ProposalView /> : <Navigate to="/login" />} />
          <Route path="/proposal/:id/edit" element={user ? <EditProposal /> : <Navigate to="/login" />} />
          <Route path="/users" element={user?.role === 'admin' ? <UserManagement /> : <Navigate to="/" />} />
          <Route path="/ai-assistant" element={user ? <AIAssistant /> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <Profile /> : <Navigate to="/login" />} />
          <Route path="/proposal/:id/mockup-designer" element={user ? <MockupDesigner /> : <Navigate to="/login" />} />
          <Route path="/contacts" element={user ? <Contacts /> : <Navigate to="/login" />} />
          <Route path="/contacts/:id" element={user ? <ContactDetail /> : <Navigate to="/login" />} />
          <Route path="/orders" element={user ? <OrderTracking /> : <Navigate to="/login" />} />
          <Route path="/production" element={user ? <ProductionQueues /> : <Navigate to="/login" />} />
          <Route path="/reports/completed" element={user ? <CompletedOrdersReport /> : <Navigate to="/login" />} />
          <Route path="/designs" element={user ? <DesignLibrary /> : <Navigate to="/login" />} />
          <Route path="/products" element={user ? <Products /> : <Navigate to="/login" />} />
          <Route path="/stores" element={user ? <Stores /> : <Navigate to="/login" />} />
          <Route path="/stores/:id" element={user ? <StoreDashboard /> : <Navigate to="/login" />} />
          <Route path="/stores/:id/:tab" element={user ? <StoreDashboard /> : <Navigate to="/login" />} />
          {user && storeShopRoutes()}
          <Route path="/price-matrices" element={user ? <PriceMatrices /> : <Navigate to="/login" />} />
          <Route path="/purchasing" element={user ? <Purchasing /> : <Navigate to="/login" />} />
          <Route path="/design-studio" element={user ? <DesignStudio /> : <Navigate to="/login" />} />
          <Route path="/mockup-designer" element={user ? <MockupDesignerV2 /> : <Navigate to="/login" />} />
          <Route path="/leads" element={user ? <Leads /> : <Navigate to="/login" />} />
          <Route path="/past-work" element={user ? <PastWork /> : <Navigate to="/login" />} />
          <Route path="/internal-catalog" element={user ? <InternalCatalog /> : <Navigate to="/login" />} />
          <Route path="/communications" element={user?.role === 'admin' ? <CommunicationCenter /> : <Navigate to="/" />} />
          <Route path="/quote-requests" element={user?.role === 'admin' ? <QuoteRequests /> : <Navigate to="/" />} />
          <Route path="/quote" element={user ? <QuoteBuilder /> : <Navigate to="/login" />} />
          {appOnly ? (
            <>
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/pay/:proposalId" element={<PublicPayment />} />
              <Route path="/approve/:proposalId" element={<DesignApproval />} />
              <Route path="/track/:orderId" element={<PublicOrderTracker />} />
            </>
          ) : storefrontRoutes()}
          <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
        </Routes>
        )}
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;